  import React, { useEffect, useContext, useReducer } from 'react';
  import reducer from '../reducers/order_reducer';
  import {
    UPDATE_SHIPPING_DETAILS,
    GET_ORDERS_BEGIN,
    GET_ORDERS_SUCCESS,
    GET_ORDERS_ERROR,
  } from '../actions';
  import { useUserContext } from './user_context';
  import { useCartContext } from './cart_context';
  import { toast } from 'react-toastify';
  import axios from 'axios';
  import emailjs from '@emailjs/browser';
  import { get_order_url, create_order_url } from '../utils/constants';
  import { formatPrice } from '../utils/helpers';
  import { setPixelUserData, buildContents, sumItems, metaOrderFields } from '../utils/metaPixel';

  const initialState = {
    orders_loading: false,
    orders_error: false,
    orders: [],
    shipping: {
      name: '',
      address: {
        line1: '',
        postal_code: '',
        city: '',
        state: '',
        country: '',
      },
    },
  };

  const OrderContext = React.createContext();

  // Function to send order notification email to admin
  const sendOrderNotificationEmail = async (orderData, orderItems, shippingInfo, orderBody) => {
    const serviceId = 'service_ofhnwke';
    const templateId = 'template_order_notificat'; // Order notification template
    const publicKey = '0tAEy-0sPHYodpkGO';

    // Format order items for email
    const itemsList = orderItems.map((item, index) => {
      const variantStr = item.variantSelections && Object.keys(item.variantSelections).length > 0
        ? Object.entries(item.variantSelections).map(([k, v]) => `${k}: ${v}`).join(' | ')
        : `Color: ${item.color || 'N/A'} | Size: ${item.size || 'N/A'}`;
      return `${index + 1}. ${item.name}\n   ${variantStr}\n   Quantity: ${item.quantity} x ${formatPrice(item.price)} = ${formatPrice(item.quantity * item.price)}`;
    }).join('\n\n');

    const templateParams = {
      order_id: orderData._id || orderData.id || 'N/A',
      order_date: new Date().toLocaleString('en-US', { 
        dateStyle: 'medium', 
        timeStyle: 'short' 
      }),
      customer_name: orderBody.name,
      customer_email: orderBody.email,
      customer_phone: shippingInfo.phoneNumber,
      shipping_address: `${shippingInfo.address}, ${shippingInfo.city}, ${shippingInfo.state}, ${shippingInfo.country} ${shippingInfo.pinCode}`,
      order_items: itemsList,
      items_count: orderItems.length,
      subtotal: formatPrice(orderBody.itemsPrice),
      shipping_fee: formatPrice(orderBody.shippingPrice),
      total_amount: formatPrice(orderBody.totalPrice),
      payment_method: orderBody.paymentInfo.status,
      admin_email: 'abdelhakcharrada2000@gmail.com',
    };

    await emailjs.send(serviceId, templateId, templateParams, publicKey);
  };

  export const OrderProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { currentUser } = useUserContext();
    const { cart, total_amount, shipping_fee } = useCartContext();

    const fetchOrders = async () => {
      if (!currentUser || !currentUser.email) {
        return;
      }
      dispatch({ type: GET_ORDERS_BEGIN });
      try {
        const response = await axios.post(get_order_url, {
          email: currentUser.email,
        });
        const orders = response.data.data || [];
        dispatch({ type: GET_ORDERS_SUCCESS, payload: orders });
      } catch (error) {
        dispatch({ type: GET_ORDERS_ERROR });
      }
    };

    const placeOrder = async () => {
      const shippingInfo = {
        address: state.shipping.address.line1,
        city: state.shipping.address.city,
        state: state.shipping.address.state,
        country: state.shipping.address.country,
        pinCode: state.shipping.address.postal_code,
        phoneNumber: state.shipping.phone_number,
      };
      const orderItems = cart.map((item) => {
        // Build display-friendly variant selections
        const displayVariants = {};
        if (item.variantSelections && Object.keys(item.variantSelections).length > 0) {
          Object.entries(item.variantSelections).forEach(([k, v]) => {
            displayVariants[k] = typeof v === 'object' && v !== null ? (v.name || v.hex || String(v)) : (v || '');
          });
        }
        return {
          name: item.name,
          price: item.price,
          quantity: item.amount,
          image: item.image,
          color: item.color || '',
          size: item.size || '',
          variantSelections: displayVariants,
          product: item.productId || item.id,
        };
      });
      // UPDATED: Changed from online payment to Cash on Delivery
      const paymentInfo = {
        id: 'COD_' + Date.now(),
        status: 'Cash on Delivery',
      };
      const eventId = "order_" + Date.now();
      
      const body = {
        name: state.shipping.name,
        email: currentUser ? currentUser.email : 'guest@cashondelivery.com',
        shippingInfo,
        orderItems,
        paymentInfo,
        itemsPrice: total_amount,
        shippingPrice: shipping_fee,
        totalPrice: total_amount + shipping_fee,
        eventId, // Pass eventId to backend for Meta CAPI deduplication
        ...metaOrderFields(), // fbp/fbc/eventSourceUrl for the Conversions API
      };
      try {
        const createResponse = await axios.post(create_order_url, body);
        
        const orderData = createResponse.data.data || createResponse.data;
        
        // Send email notification to admin
        try {
          await sendOrderNotificationEmail(orderData, orderItems, shippingInfo, body);
        } catch (emailError) {
          // Don't fail the order if email fails
        }
        
        // Track Purchase with Meta Pixel (Advanced Matching + per-item prices)
        if (window.fbq) {
          setPixelUserData({
            name: state.shipping.name,
            email: currentUser ? currentUser.email : undefined,
            phone: shippingInfo.phoneNumber,
            city: shippingInfo.city,
            state: shippingInfo.state,
            zip: shippingInfo.pinCode,
          });
          const contents = buildContents(
            orderItems.map((i) => ({ id: i.product, price: i.price, quantity: i.quantity }))
          );
          window.fbq('track', 'Purchase', {
            value: Number(total_amount + shipping_fee) || 0,
            currency: 'TND',
            content_ids: contents.map((c) => c.id),
            content_type: 'product',
            contents,
            num_items: sumItems(contents),
            order_id: orderData?._id || orderData?.id,
          }, {
            eventID: eventId
          });
        }

        toast.success('Order placed successfully! You will pay on delivery.');
        // Refetch orders after successfully placing an order
        if (currentUser && currentUser.email) {
          const response = await axios.post(get_order_url, {
            email: currentUser.email,
          });
          const orders = response.data.data || [];
          dispatch({ type: GET_ORDERS_SUCCESS, payload: orders });
        }
      } catch (error) {
        toast.error(error.message);
      }
    };

    const updateShipping = (e) => {
      const name = e.target.name;
      const value = e.target.value;
      dispatch({ type: UPDATE_SHIPPING_DETAILS, payload: { name, value } });
    };

    useEffect(() => {
      if (currentUser && currentUser.email) {
        fetchOrders();
      }
      // eslint-disable-next-line
    }, [currentUser]);

    return (
      <OrderContext.Provider value={{ ...state, updateShipping, placeOrder, fetchOrders }}>
        {children}
      </OrderContext.Provider>
    );
  };
  // make sure use
  export const useOrderContext = () => {
    return useContext(OrderContext);
  };
